/**
 * BagScene — G5/G6/G7/G9/K4 (bucket G PR 3).
 *
 * Single scrollable list with category section headers (ITEMS / KEY
 * ITEMS as sections, NOT tabs — checklist-locked). Tapping a heal/cure
 * item enters an in-panel target-picking state listing party members
 * with Lv + HP + status; tapping a member applies the effect via the
 * pure bagUse module. Cartridge no-op rule surfaces as an inline
 * message ("It won't have any effect.") with the item unconsumed.
 * Key items set their FLAG_USED_* bit and show flavor lines; never
 * consumed.
 *
 * Target picking is IN-PANEL rather than on the party orbs: the orb
 * strip is deliberately suppressed while any modal overlay is open
 * (setModalOverlayOpen → setInteractionsDisabled), and re-enabling it
 * selectively would punch a hole through that suppress chain for one
 * flow. The in-panel rows also carry exact HP numerics + status the
 * decision needs, which orbs summarize only as ring fill.
 *
 * Lifecycle: matches SettingsScene/PokedexScene — live path renders as
 * a transparent overlay over an awake WorldScene; frozen path
 * (reduced motion) claims the world snapshot. close() mirrors both.
 */
import Phaser from 'phaser';
import type { SnapshotHandle } from '../render/snapshotBackdrop';
import { ITEMS, type ItemDef } from '../data/items';
import { runState, type PartyMemberStatus } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { audio } from '../systems/audio';
import {
  itemNeedsTarget,
  useItemOnMember,
  useKeyItem,
} from '../state/bagUse';

type OverlayMode = 'live' | 'frozen';
type PanelMode = 'list' | 'target';

export interface BagSceneData {
  snapshotHandle?: SnapshotHandle | null;
}

/** Status pip fill per condition — state tokens, never type colors. */
const STATUS_CHIP_VAR: Record<Exclude<PartyMemberStatus, null>, string> = {
  psn: 'var(--status-psn)',
  tox: 'var(--status-psn)',
  par: 'var(--status-par)',
  brn: 'var(--status-brn)',
  slp: 'var(--status-slp)',
  frz: 'var(--status-frz)',
};

function hpThresholdVar(current: number, max: number): string {
  const frac = max > 0 ? current / max : 0;
  if (frac > 0.5) return 'var(--ok-green)';
  if (frac >= 0.25) return 'var(--warn-amber)';
  return 'var(--err-red)';
}

function displayName(species: string): string {
  return species.charAt(0).toUpperCase() + species.slice(1);
}

export class BagScene extends Phaser.Scene {
  private modalRoot: HTMLDivElement | null = null;
  private bodyEl: HTMLDivElement | null = null;
  private messageEl: HTMLParagraphElement | null = null;
  private overlayMode: OverlayMode = 'live';
  private snapshotHandle: SnapshotHandle | null = null;
  private panelMode: PanelMode = 'list';
  private pendingItemId: string | null = null;

  constructor() {
    super('BagScene');
  }

  init(data: BagSceneData): void {
    this.snapshotHandle = data.snapshotHandle ?? null;
    this.overlayMode = this.snapshotHandle ? 'frozen' : 'live';
    this.panelMode = 'list';
    this.pendingItemId = null;
  }

  create(): void {
    // Glass-Over-World PR 3b: frozen path claims the snapshot;
    // live path stays transparent so WorldScene shows through.
    if (this.snapshotHandle) {
      this.snapshotHandle.transferOwnership(this);
    } else {
      this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    }
    this.add
      .rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0)
      .setOrigin(0)
      .setInteractive()
      .on('pointerdown', () => undefined);
    this.mountDOM();

    // K4: bag-open SFX. 'menu_open' IS the cartridge bag cue — its
    // bake source is se_bag_pocket (packages/audio-bake bake.ts), so
    // no new asset is needed for the bag to sound like the bag.
    audio.playSfx('menu_open');

    this.input.keyboard?.on('keydown-ESC', () => this.handleBack());
    this.input.keyboard?.on('keydown-B', () => this.handleBack());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  // ─── DOM scaffolding ────────────────────────────────────────────

  private mountDOM(): void {
    const gameEl = document.getElementById('game');
    if (!gameEl) return;

    const root = document.createElement('div');
    root.className = 'bag-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Bag');
    // Backdrop tap: cancels targeting first; closes from the list.
    root.addEventListener('click', (e) => {
      if (e.target === root) this.handleBack();
    });

    const card = document.createElement('div');
    card.className = 'bag-card';

    const header = document.createElement('header');
    header.className = 'bag-card__header';

    const title = document.createElement('h1');
    title.className = 'bag-card__title';
    title.textContent = 'Bag';
    header.appendChild(title);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'bag-card__close';
    close.setAttribute('aria-label', 'Close bag');
    close.textContent = '×';
    close.addEventListener('click', () => this.close());
    header.appendChild(close);

    card.appendChild(header);

    const body = document.createElement('div');
    body.className = 'bag-card__body';
    card.appendChild(body);
    this.bodyEl = body;

    // Inline outcome strip — "Charmander recovered 20 HP!" / no-effect
    // lines land here rather than in the world dialogue overlay (a
    // modal is already open; nesting the Zone-2 dialogue under Zone-1
    // chrome would invert the layer model).
    const message = document.createElement('p');
    message.className = 'bag-card__msg';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.style.display = 'none';
    card.appendChild(message);
    this.messageEl = message;

    root.appendChild(card);
    gameEl.appendChild(root);
    this.modalRoot = root;

    this.renderBody();
  }

  private setMessage(lines: string[] | null): void {
    if (!this.messageEl) return;
    if (!lines || lines.length === 0) {
      this.messageEl.style.display = 'none';
      this.messageEl.textContent = '';
      return;
    }
    this.messageEl.style.display = '';
    this.messageEl.textContent = lines.join(' ');
  }

  // ─── State rendering ────────────────────────────────────────────

  private renderBody(): void {
    const body = this.bodyEl;
    if (!body) return;
    body.replaceChildren();
    if (this.panelMode === 'target' && this.pendingItemId) {
      this.renderTargetPicker(body, this.pendingItemId);
    } else {
      this.renderList(body);
    }
  }

  private renderList(body: HTMLDivElement): void {
    const bag = runState.getBag();
    const entries = Object.keys(bag)
      .map((id) => ITEMS[id])
      .filter((def): def is ItemDef => def !== undefined);

    if (entries.length === 0) {
      const headline = document.createElement('p');
      headline.className = 'bag-card__empty-headline';
      headline.textContent = 'No items yet.';
      body.appendChild(headline);
      const empty = document.createElement('p');
      empty.className = 'bag-card__empty-body';
      empty.textContent = 'Item balls are out there on the routes.';
      body.appendChild(empty);
      return;
    }

    const items = entries
      .filter((d) => d.effect.kind !== 'key')
      .sort((a, b) => a.name.localeCompare(b.name));
    const keyItems = entries
      .filter((d) => d.effect.kind === 'key')
      .sort((a, b) => a.name.localeCompare(b.name));

    const list = document.createElement('div');
    list.className = 'bag-card__list';

    const section = (label: string, defs: ItemDef[]): void => {
      if (defs.length === 0) return;
      const h = document.createElement('h2');
      h.className = 'bag-section-label';
      h.textContent = label;
      list.appendChild(h);
      for (const def of defs) list.appendChild(this.makeItemRow(def));
    };
    section('Items', items);
    section('Key items', keyItems);

    body.appendChild(list);
  }

  private makeItemRow(def: ItemDef): HTMLButtonElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'bag-row';

    const top = document.createElement('span');
    top.className = 'bag-row__top';
    const name = document.createElement('span');
    name.className = 'bag-row__name';
    name.textContent = def.name;
    top.appendChild(name);
    if (def.effect.kind !== 'key') {
      const count = document.createElement('span');
      count.className = 'bag-row__count';
      count.textContent = `×${runState.countItem(def.id)}`;
      top.appendChild(count);
    }
    row.appendChild(top);

    const desc = document.createElement('span');
    desc.className = 'bag-row__desc';
    desc.textContent = def.desc;
    row.appendChild(desc);

    row.addEventListener('click', () => this.onItemTap(def));
    return row;
  }

  private onItemTap(def: ItemDef): void {
    this.setMessage(null);
    if (itemNeedsTarget(def.id)) {
      this.panelMode = 'target';
      this.pendingItemId = def.id;
      this.renderBody();
      return;
    }
    const outcome = useKeyItem(def.id);
    if (outcome.kind === 'key-used') {
      audio.playSfx('select');
      saveManager.save();
      this.setMessage(outcome.messages);
    }
    this.renderBody();
  }

  private renderTargetPicker(body: HTMLDivElement, itemId: string): void {
    const def = ITEMS[itemId];
    if (!def || !runState.hasItem(itemId)) {
      // State drifted (count hit 0) — fall back to the list.
      this.panelMode = 'list';
      this.pendingItemId = null;
      this.renderList(body);
      return;
    }

    const prompt = document.createElement('h2');
    prompt.className = 'bag-section-label';
    prompt.textContent = `Use ${def.name} on…`;
    body.appendChild(prompt);

    const list = document.createElement('div');
    list.className = 'bag-card__list';
    for (const member of runState.getParty()) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'bag-row bag-target-row';

      const top = document.createElement('span');
      top.className = 'bag-row__top';
      const name = document.createElement('span');
      name.className = 'bag-row__name';
      name.textContent = displayName(member.species);
      top.appendChild(name);
      const lv = document.createElement('span');
      lv.className = 'bag-row__count';
      lv.textContent = `Lv ${member.level}`;
      top.appendChild(lv);
      row.appendChild(top);

      const meta = document.createElement('span');
      meta.className = 'bag-row__desc';
      const hp = document.createElement('span');
      hp.className = 'bag-target-row__hp';
      hp.textContent = `${member.hp.current}/${member.hp.max} HP`;
      hp.style.color = hpThresholdVar(member.hp.current, member.hp.max);
      meta.appendChild(hp);
      if (member.status) {
        const chip = document.createElement('span');
        chip.className = 'bag-status-chip';
        chip.style.background = STATUS_CHIP_VAR[member.status];
        chip.setAttribute('aria-label', `status ${member.status}`);
        meta.appendChild(chip);
      }
      row.appendChild(meta);

      row.addEventListener('click', () => this.onTargetTap(itemId, member.id));
      list.appendChild(row);
    }
    body.appendChild(list);

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'bag-cancel';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', () => this.exitTargeting());
    body.appendChild(cancel);
  }

  private onTargetTap(itemId: string, memberId: string): void {
    const outcome = useItemOnMember(itemId, memberId);
    switch (outcome.kind) {
      case 'healed':
      case 'cured':
        audio.playSfx('heal');
        saveManager.save();
        this.setMessage([outcome.message]);
        this.exitTargeting();
        break;
      case 'no-effect':
        // Cartridge: item not consumed; stay in targeting so the
        // player can pick someone else.
        this.setMessage([outcome.message]);
        break;
      case 'invalid':
        this.setMessage(null);
        this.exitTargeting();
        break;
      default:
        this.exitTargeting();
    }
  }

  private exitTargeting(): void {
    this.panelMode = 'list';
    this.pendingItemId = null;
    this.renderBody();
  }

  /** ESC / B / backdrop: one step back — targeting → list; list → close. */
  private handleBack(): void {
    if (this.panelMode === 'target') {
      this.setMessage(null);
      this.exitTargeting();
      return;
    }
    this.close();
  }

  private close(): void {
    audio.playSfx('select');
    if (this.overlayMode === 'live') {
      const world = this.scene.get('WorldScene') as Phaser.Scene & {
        setModalOverlayOpen?: (open: boolean, source: 'bag') => void;
      };
      world.setModalOverlayOpen?.(false, 'bag');
      this.scene.stop();
    } else {
      this.scene.stop();
      this.scene.wake('WorldScene');
    }
  }

  private teardown(): void {
    this.modalRoot?.remove();
    this.modalRoot = null;
    this.bodyEl = null;
    this.messageEl = null;
  }
}
